import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Phone, X } from 'lucide-react';
import { CALENDLY_ONBOARDING_URL } from '@/config/support';

interface TrialHelpBannerProps {
  planType: string | null | undefined;
  establishmentId: string | null | undefined;
  hasReservations: boolean;
}

const STORAGE_KEY = 'trialHelpBannerDismissed';
const RESHOW_DELAY_DAYS = 5;

export function TrialHelpBanner({ planType, establishmentId, hasReservations }: TrialHelpBannerProps) {
  const [dismissed, setDismissed] = useState(true); // Start hidden to avoid flash

  useEffect(() => {
    if (!establishmentId) return;

    // Non-trial users: always hidden
    if (planType !== 'trial') {
      setDismissed(true);
      return;
    }

    // Has reservations: user doesn't need help anymore
    if (hasReservations) {
      setDismissed(true);
      return;
    }

    const key = `${STORAGE_KEY}_${establishmentId}`;
    const storedValue = localStorage.getItem(key);

    if (!storedValue) {
      // Never dismissed → show
      setDismissed(false);
      return;
    }

    // Check if stored value is old boolean format
    if (storedValue === 'true') {
      // Migrate: treat as dismissed now, set timestamp
      localStorage.setItem(key, new Date().toISOString());
      setDismissed(true);
      return;
    }

    // Timestamp format: check if 5+ days elapsed
    const dismissedAt = new Date(storedValue);
    const daysSinceDismiss = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);

    if (daysSinceDismiss >= RESHOW_DELAY_DAYS) {
      setDismissed(false);
    } else {
      setDismissed(true);
    }
  }, [establishmentId, planType, hasReservations]);

  if (planType !== 'trial' || hasReservations || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    if (establishmentId) {
      localStorage.setItem(`${STORAGE_KEY}_${establishmentId}`, new Date().toISOString());
    }
    setDismissed(true);
  };

  const handleBookCall = () => {
    window.open(CALENDLY_ONBOARDING_URL, '_blank');
  };

  return (
    <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 shrink-0">
            <Phone className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium">Des questions ?</p>
            <p className="text-xs text-muted-foreground hidden sm:block">
              Réservez un appel de configuration gratuit de 30 minutes
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" onClick={handleBookCall}>
            Planifier un appel
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            onClick={handleDismiss}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
